import { useEffect, useRef, useState } from "react";

/**
 * Keeps a loading flag "true" for at least `minMs` after loading starts,
 * so skeleton/loaders don't flash-and-vanish when data resolves instantly
 * (e.g. cached React Query responses in local dev).
 *
 * @param {boolean} isLoading - the real loading flag from the data source
 * @param {number} minMs - minimum time the loading state stays visible (default 600ms)
 * @returns {boolean} - the (possibly extended) loading flag
 */
export function useMinLoadingTime(isLoading, minMs = 600) {
  const [show, setShow] = useState(isLoading);
  const startRef = useRef(null);

  useEffect(() => {
    if (isLoading) {
      if (startRef.current == null) startRef.current = Date.now();
      setShow(true);
      return;
    }

    if (startRef.current == null) {
      setShow(false);
      return;
    }

    const elapsed = Date.now() - startRef.current;
    const remaining = Math.max(0, minMs - elapsed);
    const timer = setTimeout(() => {
      setShow(false);
      startRef.current = null;
    }, remaining);
    return () => clearTimeout(timer);
  }, [isLoading, minMs]);

  return show;
}

export default useMinLoadingTime;
